import { CheckCircle2 } from 'lucide-react'
import { useEffect, useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError, describeApiError } from '@/api/problemDetails'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui'
import {
  useSendDigestNow,
  useTriageSettings,
  useUpdateTriageSettings,
} from '@/features/triage/queries'
import type { TriageSettings } from '@/features/triage/types'
import { cn } from '@/lib/utils'

import { TIME_ZONES } from '../handoffPolicy'

const SAVED_FEEDBACK_MS = 4_000

const fieldClass =
  'rounded-md border border-border-default bg-background-elevated px-3 text-sm text-foreground-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:opacity-60'

interface Draft {
  readonly enabled: boolean
  readonly transcribeAudio: boolean
  readonly quietWindowMinutes: number
  readonly digestEnabled: boolean
  readonly digestTime: string
  readonly timeZoneId: string
  readonly digestRecipientNumber: string
}

function toDraft(settings: TriageSettings): Draft {
  return {
    enabled: settings.enabled,
    transcribeAudio: settings.transcribeAudio,
    quietWindowMinutes: settings.quietWindowMinutes,
    digestEnabled: settings.digestEnabled,
    digestTime: settings.digestTime,
    timeZoneId: settings.timeZoneId,
    digestRecipientNumber: settings.digestRecipientNumber ?? '',
  }
}

/**
 * Triagem (ADR-028): ligar o assistente de pendências, transcrever áudios, janela de silêncio e o resumo diário
 * (hora, fuso, número destinatário). Nasce tudo desligado. Some para quem não gerencia a organização (403).
 */
export function TriageSettingsCard({ tenantId }: { readonly tenantId: string }) {
  const { t } = useTranslation()
  const settingsQuery = useTriageSettings(tenantId)

  if (settingsQuery.error instanceof ApiError && settingsQuery.error.problem.status === 403) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.triage.title')}</CardTitle>
        <CardDescription>{t('settings.triage.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {settingsQuery.data === undefined ? (
          settingsQuery.isError ? (
            <Alert variant="danger" title={t('settings.triage.loadFailed')} />
          ) : (
            <Spinner label={t('loading')} />
          )
        ) : (
          <TriageSettingsForm tenantId={tenantId} settings={settingsQuery.data} />
        )}
      </CardContent>
    </Card>
  )
}

function TriageSettingsForm({
  tenantId,
  settings,
}: {
  readonly tenantId: string
  readonly settings: TriageSettings
}) {
  const { t } = useTranslation()
  const timeZoneLabels: Readonly<Record<string, string>> = {
    'America/Sao_Paulo': t('settings.handoff.timeZones.America_Sao_Paulo'),
    'America/Bahia': t('settings.handoff.timeZones.America_Bahia'),
    'America/Fortaleza': t('settings.handoff.timeZones.America_Fortaleza'),
    'America/Recife': t('settings.handoff.timeZones.America_Recife'),
    'America/Belem': t('settings.handoff.timeZones.America_Belem'),
    'America/Manaus': t('settings.handoff.timeZones.America_Manaus'),
    'America/Cuiaba': t('settings.handoff.timeZones.America_Cuiaba'),
    'America/Campo_Grande': t('settings.handoff.timeZones.America_Campo_Grande'),
    'America/Porto_Velho': t('settings.handoff.timeZones.America_Porto_Velho'),
    'America/Boa_Vista': t('settings.handoff.timeZones.America_Boa_Vista'),
    'America/Rio_Branco': t('settings.handoff.timeZones.America_Rio_Branco'),
    'America/Noronha': t('settings.handoff.timeZones.America_Noronha'),
  }
  const mutation = useUpdateTriageSettings(tenantId)
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings))
  const [showSaved, setShowSaved] = useState(false)

  useEffect(() => {
    if (!showSaved) {
      return
    }
    const timer = setTimeout(() => {
      setShowSaved(false)
    }, SAVED_FEEDBACK_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [showSaved])

  function update(patch: Partial<Draft>): void {
    setDraft((current) => ({ ...current, ...patch }))
    setShowSaved(false)
    if (mutation.isError) {
      mutation.reset()
    }
  }

  const quietWindowInvalid = draft.quietWindowMinutes < 1 || draft.quietWindowMinutes > 30
  // "Enviar agora" usa o que está SALVO no servidor: com alterações pendentes, primeiro salvar.
  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(settings))

  function submit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()
    if (quietWindowInvalid || mutation.isPending) {
      return
    }

    mutation.mutate(
      {
        enabled: draft.enabled,
        transcribeAudio: draft.transcribeAudio,
        quietWindowMinutes: draft.quietWindowMinutes,
        historyWindowMessages: settings.historyWindowMessages,
        historyWindowChars: settings.historyWindowChars,
        dailyRunCap: settings.dailyRunCap,
        digestEnabled: draft.digestEnabled,
        digestTime: draft.digestTime,
        timeZoneId: draft.timeZoneId,
        digestRecipientNumber:
          draft.digestRecipientNumber.trim() === '' ? null : draft.digestRecipientNumber.trim(),
      },
      {
        onSuccess: (saved) => {
          setDraft(toDraft(saved))
          setShowSaved(true)
        },
      },
    )
  }

  return (
    <form
      onSubmit={(event) => {
        submit(event)
      }}
      className="flex flex-col gap-5"
    >
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-sm font-semibold text-foreground-primary">
          {t('settings.triage.general.title')}
        </legend>
        <label className="flex items-start gap-2 text-sm text-foreground-secondary">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={draft.enabled}
            onChange={(event) => {
              update({ enabled: event.target.checked })
            }}
          />
          <span className="flex flex-col">
            <span>{t('settings.triage.general.enable')}</span>
            <span className="text-xs text-foreground-muted">
              {t('settings.triage.general.enableHint')}
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-foreground-secondary">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={draft.transcribeAudio}
            onChange={(event) => {
              update({ transcribeAudio: event.target.checked })
            }}
          />
          <span className="flex flex-col">
            <span>{t('settings.triage.general.transcribe')}</span>
            <span className="text-xs text-foreground-muted">
              {t('settings.triage.general.transcribeHint')}
            </span>
          </span>
        </label>
        <label className="flex flex-col gap-1.5 text-sm sm:max-w-xs">
          <span className="font-medium text-foreground-secondary">
            {t('settings.triage.general.quietWindow')}
          </span>
          <input
            type="number"
            min={1}
            max={30}
            className={cn(fieldClass, 'h-10')}
            value={draft.quietWindowMinutes}
            aria-invalid={quietWindowInvalid}
            onChange={(event) => {
              update({ quietWindowMinutes: Number(event.target.value) })
            }}
          />
          <span className="text-xs text-foreground-muted">
            {t('settings.triage.general.quietWindowHint')}
          </span>
        </label>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-sm font-semibold text-foreground-primary">
          {t('settings.triage.digest.title')}
        </legend>
        <label className="flex items-start gap-2 text-sm text-foreground-secondary">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={draft.digestEnabled}
            onChange={(event) => {
              update({ digestEnabled: event.target.checked })
            }}
          />
          <span className="flex flex-col">
            <span>{t('settings.triage.digest.enable')}</span>
            <span className="text-xs text-foreground-muted">
              {t('settings.triage.digest.enableHint')}
            </span>
          </span>
        </label>
        {draft.digestEnabled ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-foreground-secondary">
                {t('settings.triage.digest.time')}
              </span>
              <input
                type="time"
                className={cn(fieldClass, 'h-10')}
                value={draft.digestTime}
                onChange={(event) => {
                  update({ digestTime: event.target.value })
                }}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-foreground-secondary">
                {t('settings.triage.digest.timeZone')}
              </span>
              <select
                className={cn(fieldClass, 'h-10')}
                value={draft.timeZoneId}
                onChange={(event) => {
                  update({ timeZoneId: event.target.value })
                }}
              >
                {(TIME_ZONES as readonly string[]).includes(draft.timeZoneId) ? null : (
                  <option value={draft.timeZoneId}>{draft.timeZoneId}</option>
                )}
                {TIME_ZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {timeZoneLabels[zone] ?? zone}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-foreground-secondary">
                {t('settings.triage.digest.recipient')}
              </span>
              <input
                type="tel"
                inputMode="numeric"
                className={cn(fieldClass, 'h-10 sm:max-w-xs')}
                value={draft.digestRecipientNumber}
                placeholder={t('settings.triage.digest.recipientPlaceholder')}
                onChange={(event) => {
                  update({ digestRecipientNumber: event.target.value })
                }}
              />
              <span className="text-xs text-foreground-muted">
                {t('settings.triage.digest.recipientHint')}
              </span>
            </label>
          </div>
        ) : null}
        {draft.digestEnabled || settings.lastDigestErrorCode !== null ? (
          <DigestStatus tenantId={tenantId} settings={settings} dirty={dirty} />
        ) : null}
      </fieldset>

      {mutation.isError ? (
        <Alert variant="danger" title={t('settings.triage.saveFailed')}>
          <ul className="list-disc pl-4">
            {describeApiError(mutation.error).map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" loading={mutation.isPending} disabled={quietWindowInvalid}>
          {t('settings.triage.save')}
        </Button>
        {showSaved ? (
          <span
            className="inline-flex items-center gap-1 text-sm text-status-success"
            role="status"
          >
            <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
            {t('settings.triage.saved')}
          </span>
        ) : null}
      </div>
    </form>
  )
}

/**
 * Estado do resumo diário: aviso quando o último NÃO saiu (motivo em linguagem do dono), data do último envio e o
 * botão "Enviar resumo agora" — teste do dono, não altera o envio agendado.
 */
function DigestStatus({
  tenantId,
  settings,
  dirty,
}: {
  readonly tenantId: string
  readonly settings: TriageSettings
  readonly dirty: boolean
}) {
  const { t, i18n } = useTranslation()
  const sendNow = useSendDigestNow(tenantId)
  const errorMessages: Readonly<Record<string, string>> = {
    recipient_unavailable: t('settings.triage.digest.lastError.recipientUnavailable'),
    channel_unavailable: t('settings.triage.digest.lastError.channelUnavailable'),
    send_failed: t('settings.triage.digest.lastError.sendFailed'),
  }
  const lastSent =
    settings.lastDigestSentOnLocalDate === null
      ? null
      : formatLocalDate(settings.lastDigestSentOnLocalDate, i18n.language)

  return (
    <div className="flex flex-col gap-3">
      {settings.lastDigestErrorCode !== null && !sendNow.isError ? (
        <Alert variant="warning" title={t('settings.triage.digest.lastError.title')}>
          {errorMessages[settings.lastDigestErrorCode] ??
            t('settings.triage.digest.lastError.unknown')}
        </Alert>
      ) : null}
      {sendNow.isError ? (
        <Alert variant="danger" title={t('settings.triage.digest.sendNowFailed')}>
          <ul className="list-disc pl-4">
            {describeApiError(sendNow.error).map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={sendNow.isPending}
          disabled={dirty}
          onClick={() => {
            sendNow.mutate()
          }}
        >
          {t('settings.triage.digest.sendNow')}
        </Button>
        {sendNow.isSuccess ? (
          <span
            className="inline-flex items-center gap-1 text-sm text-status-success"
            role="status"
          >
            <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
            {t('settings.triage.digest.sendNowSent')}
          </span>
        ) : null}
        {lastSent !== null ? (
          <span className="text-xs text-foreground-muted">
            {t('settings.triage.digest.lastSent', { date: lastSent })}
          </span>
        ) : null}
      </div>
      <span className="text-xs text-foreground-muted">
        {dirty ? t('settings.triage.digest.sendNowDirty') : t('settings.triage.digest.sendNowHint')}
      </span>
    </div>
  )
}

/** "yyyy-MM-dd" (data LOCAL da organização) → data curta no idioma da UI, sem conversão de fuso. */
function formatLocalDate(isoDate: string, language: string): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  if (year === undefined || month === undefined || day === undefined) {
    return isoDate
  }
  return new Intl.DateTimeFormat(language, { dateStyle: 'short' }).format(
    new Date(year, month - 1, day),
  )
}
